import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SidePanelPlumbingTab from '../SidePanelPlumbingTab';
import * as St from './DesignSidePanel.styles';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { usePlumbing } from '~/api/easy-estimator-api/plumbing-api/usePlumbing';
import { DesignSection, DesignStepName, StepName } from '~/core/enums';
import { useClearSelectionCircles } from '~/core/hooks/useClearSelectionCircles';
import AddOnOptions from '~/icons/AddOnOptions.svg';
import Cooktop from '~/icons/Cooktop.svg';
import Corner from '~/icons/Corner.svg';
import CustomSide from '~/icons/CustomSide.svg';
import Cutouts from '~/icons/Cutouts.svg';
import DiagonalCorner from '~/icons/DiagonalCorner.svg';
import EdgeTabIcon from '~/icons/EdgeTab.svg';
import Plumbing from '~/icons/Plumbing.svg';
import Shape from '~/icons/Shape.svg';
import Sink from '~/icons/Sink.svg';
import TearOut from '~/icons/TearOut.svg';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  allSteps,
  goToStep,
  isADesignStep,
  selectCurrentStep,
  selectEnabledSteps,
  selectSteps,
  updateSteps,
} from '~/redux/slices/stepper';
import { requiredSteps } from '~/redux/slices/stepper/stepper.contants';
import { selectVisitedSteps } from '~/redux/slices/visitedSteps';
import SidePanelAddOnOptionsTab from '~organisms/SidePanelAddOnOptionsTab';
import SidePanelCookingApplianceTab from '~organisms/SidePanelCookingApplianceTab';
import SidePanelCornerRadiusTab from '~organisms/SidePanelCornerRadiusTab';
import SidePanelCustomSideTab from '~organisms/SidePanelCustomSideTab';
import SidePanelCutoutsTab from '~organisms/SidePanelCutoutsTab';
import SidePanelDiagonalCornerTab from '~organisms/SidePanelDiagonalCornerTab';
import SidePanelEdgeTab from '~organisms/SidePanelEdgeTab';
import SidePanelShapeTab from '~organisms/SidePanelShapeTab';
import SidePanelSinksTab from '~organisms/SidePanelSinksTab';
import SidePanelTearoutsTab from '~organisms/SidePanelTearoutsTab';

type TabContent = {
  IconComponent: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  ContentComponent: () => JSX.Element | null;
  /** uses translation string */
  section: DesignSection;
};

const tabContent: Record<DesignStepName, TabContent> = {
  [StepName.designShape]: {
    IconComponent: Shape,
    ContentComponent: SidePanelShapeTab,
    section: DesignSection.SHAPES,
  },
  [StepName.designEdge]: {
    IconComponent: EdgeTabIcon,
    ContentComponent: SidePanelEdgeTab,
    section: DesignSection.EDGES,
  },
  [StepName.designCornerRadius]: {
    IconComponent: Corner,
    ContentComponent: SidePanelCornerRadiusTab,
    section: DesignSection.CORNER_RADIUS,
  },
  [StepName.designDiagonalCorner]: {
    IconComponent: DiagonalCorner,
    ContentComponent: SidePanelDiagonalCornerTab,
    section: DesignSection.DIAGONAL_CORNERS,
  },
  [StepName.designCustomSide]: {
    IconComponent: CustomSide,
    ContentComponent: SidePanelCustomSideTab,
    section: DesignSection.CUSTOM_SIDES,
  },
  [StepName.designSink]: {
    IconComponent: Sink,
    ContentComponent: SidePanelSinksTab,
    section: DesignSection.SINKS,
  },
  [StepName.designCookingAppliance]: {
    IconComponent: Cooktop,
    ContentComponent: SidePanelCookingApplianceTab,
    section: DesignSection.COOKING_APPLIANCES,
  },
  [StepName.designCutout]: {
    IconComponent: Cutouts,
    ContentComponent: SidePanelCutoutsTab,
    section: DesignSection.CUTOUTS,
  },
  [StepName.designPlumbing]: {
    IconComponent: Plumbing,
    ContentComponent: SidePanelPlumbingTab,
    section: DesignSection.PLUMBING,
  },
  [StepName.designTearOut]: {
    IconComponent: TearOut,
    ContentComponent: SidePanelTearoutsTab,
    section: DesignSection.TEAR_OUTS,
  },
  [StepName.designAddOn]: {
    IconComponent: AddOnOptions,
    ContentComponent: SidePanelAddOnOptionsTab,
    section: DesignSection.OPTIONAL_PRODUCTS,
  },
};

const isATabContent = (step: StepName): step is keyof typeof tabContent =>
  step in tabContent;
const getTabContent = (tabStep: StepName) =>
  isATabContent(tabStep) ? tabContent[tabStep] : tabContent.designShape;

type DesignSidePanelProps = {
  isExpanded?: boolean;
};

const DesignSidePanel = ({ isExpanded = true }: DesignSidePanelProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const visitedTabs = useAppSelector(selectVisitedSteps);
  const currentStep = useAppSelector(selectCurrentStep);
  const enabledSteps = useAppSelector(selectEnabledSteps);
  const steps = useAppSelector(selectSteps);
  const { ContentComponent } = getTabContent(currentStep);
  const { isFetchingEdges, sortedEdges } = useEdges();
  const { isFetchingCornerRadii, cornerRadii } = useCornerRadii();
  const { isFetchingDiagonalCorners, diagonalCorners } = useDiagonalCorners();
  const { isFetchingPlumbing, plumbingItems } = usePlumbing();

  const { clearSelectionCircles } = useClearSelectionCircles();

  useEffect(() => {
    const doesSectionHasProducts: Record<string, () => boolean> = {
      [StepName.designEdge]: () => !isFetchingEdges && sortedEdges.length > 0,
      [StepName.designCornerRadius]: () =>
        !isFetchingCornerRadii && cornerRadii.length > 0,
      [StepName.designDiagonalCorner]: () =>
        !isFetchingDiagonalCorners && diagonalCorners.length > 0,
      [StepName.designPlumbing]: () =>
        !isFetchingPlumbing && plumbingItems.length > 0,
    };
    const updatedSteps = allSteps.filter(
      (step) => doesSectionHasProducts[step]?.() ?? true
    );

    dispatch(updateSteps(updatedSteps));
  }, [
    cornerRadii.length,
    diagonalCorners.length,
    dispatch,
    isFetchingCornerRadii,
    isFetchingDiagonalCorners,
    isFetchingEdges,
    isFetchingPlumbing,
    plumbingItems.length,
    sortedEdges.length,
  ]);

  return (
    <St.Container aria-expanded={isExpanded}>
      <St.MenuBarBackdrop />
      <St.MenuBar role="tablist">
        {steps.filter(isADesignStep).map((tab) => {
          const { IconComponent, section } = getTabContent(tab);

          const isCurrentStep = currentStep === tab;

          const isRequiredStep = requiredSteps[tab]?.required ?? false;

          return (
            <St.MenuBarButton
              key={tab}
              role="tab"
              aria-selected={isCurrentStep}
              data-invert-color-selected={tab === StepName.designTearOut}
              data-invert-color-not-selected={tab === StepName.designAddOn}
              data-is-visited={visitedTabs.includes(tab as DesignStepName)}
              onClick={() => {
                clearSelectionCircles();
                dispatch(goToStep(tab));
              }}
              disabled={!enabledSteps.includes(tab)}
            >
              {isRequiredStep && (
                <St.AsteriskContainer $isSelected={isCurrentStep}>
                  *
                </St.AsteriskContainer>
              )}
              <St.BtnLabel>
                {t(`DESIGN_SIDE_PANEL.${section}.LABEL`)}
              </St.BtnLabel>

              <St.MenuBarIcon
                component={() => (
                  <IconComponent
                    aria-hidden="true"
                    focusable="false"
                    fill="currentColor"
                  />
                )}
              />
            </St.MenuBarButton>
          );
        })}
      </St.MenuBar>

      <St.PanelContent>
        <ContentComponent />
      </St.PanelContent>
    </St.Container>
  );
};

export default DesignSidePanel;
